import { useState, type ReactNode } from "react";
import { FieldArray, useFormikContext } from "formik";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconFileImport, IconGripVertical } from "@tabler/icons-react";
import EdlItemNomEtatInputs from "./EdlItemNomEtatInputs";
import {
  EDL_ITEM_CHILD_VALUE,
  EDL_ITEM_TEMPLATE_CHILD_VALUE,
  edlItemChildValidationSchema,
  // @ts-expect-error pure js
} from "../../models/edl";
// @ts-expect-error pure js
import { toInitialValuesFromSchema } from "../../models/utils";
// @ts-expect-error pure js
import TextArea from "../UI/TextArea";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Title from "../UI/Title";
import DropdownMenu from "../UI/DropdownMenu";
import IconButton from "../UI/IconButton";
import EdlPhotoField from "./EdlPhotoField";
import EdlEtatPills from "./EdlEtatPills";
import EdlAddButton from "./EdlAddButton";
import styles from "./EdlItemFormInputs.module.css";

const TYPE_EDLS = [
  { type: "edl", nom: "Bâti" },
  { type: "equipements", nom: "Équipements" },
  { type: "meubles", nom: "Mobilier" },
] as const;

type EdlSubItem = {
  id?: number;
  nom?: string;
  note?: string | number;
};

type EdlChildItem = {
  id?: number;
  type?: string;
  nom?: string;
  note?: string | number;
  materiaux: string[];
  degradations: string[];
  template_materiaux?: number | string;
  quantite?: number | string;
  commentaire?: string;
  items?: EdlSubItem[];
  photos?: unknown[];
};

type EdlItem = {
  id?: number;
  nom?: string;
  note?: string | number;
  items?: EdlChildItem[];
  materiaux: string[];
  degradations: string[];
};

type EdlItemFormInputsProps = {
  item?: EdlItem;
  inputNamePrefix?: string;
  isTemplate?: boolean;
  otherRooms?: EdlItem[];
};

/* Carte d'élément déplaçable : l'id dnd est l'index de l'élément dans le
 * tableau Formik `items`, qui mélange les trois catégories. Les modèles de
 * pièce n'ont pas de poignée : leur ordre n'est pas conservé en base. */
function SortableElement({
  id,
  children,
}: {
  id: number;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };
  const handle = (
    <IconButton
      ref={setActivatorNodeRef}
      icon={IconGripVertical}
      label="Déplacer l'élément"
      variant="plain"
      className={styles.dragHandle}
      {...attributes}
      {...listeners}
    />
  );
  return (
    <div ref={setNodeRef} style={style} className={styles.sortableElement}>
      {children(handle)}
    </div>
  );
}

/* Réordonne les éléments d'une catégorie sans toucher aux autres : les
 * éléments déplacés reprennent, dans leur nouvel ordre, les emplacements que la
 * catégorie occupait déjà dans `items`. */
function moveWithinType<T>(
  items: T[],
  typeIndexes: number[],
  from: number,
  to: number,
): T[] {
  const reordered = arrayMove(typeIndexes, from, to);
  const next = [...items];
  typeIndexes.forEach((slot, i) => {
    next[slot] = items[reordered[i]];
  });
  return next;
}

/* Un élément tel que servi par l'API a ses champs vides à null, que le schéma
 * de validation refuse : on le ramène aux mêmes valeurs qu'à l'ouverture d'une
 * pièce (chaînes vides). */
function fromApi(element: EdlChildItem): EdlChildItem {
  return toInitialValuesFromSchema(
    edlItemChildValidationSchema,
    element,
    EDL_ITEM_CHILD_VALUE,
  );
}

/* Copie sans les identifiants ni les photos, qui appartiennent à l'original. */
function cloneElement(element: EdlChildItem, nom?: string): EdlChildItem {
  return {
    ...element,
    id: undefined,
    nom: nom ?? element.nom,
    photos: [],
    items: (element.items ?? []).map((sub) => ({
      ...sub,
      id: undefined,
      photos: [],
    })),
  };
}

export default function EdlItemFormInputs({
  item,
  inputNamePrefix = "",
  isTemplate,
  otherRooms = [],
}: EdlItemFormInputsProps) {
  const { setFieldValue } = useFormikContext();
  // Index (dans `items`) du seul élément déplié ; null quand tout est replié.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const items = item?.items ?? [];

  function applyNoteToAll(value: string) {
    setFieldValue(`${inputNamePrefix}note`, value);
    if (!value) return;
    items.forEach((child, index) => {
      if (!child.note || child.note === "") {
        setFieldValue(`${inputNamePrefix}items[${index}].note`, value);
      }
    });
  }

  function setItems(next: EdlChildItem[]) {
    setFieldValue(`${inputNamePrefix}items`, next);
  }

  function appendElement(element: EdlChildItem) {
    setItems([...items, element]);
    setOpenIndex(items.length);
  }

  function removeElement(index: number) {
    setItems(items.filter((_, i) => i !== index));
    if (openIndex === null) return;
    if (openIndex === index) setOpenIndex(null);
    else if (openIndex > index) setOpenIndex(openIndex - 1);
  }

  const hasItemWithoutState = items.some(
    (child) => !child.note || child.note === "",
  );

  return (
    <div className={styles.container}>
      <EdlItemNomEtatInputs inputNamePrefix={inputNamePrefix} />

      {!isTemplate && hasItemWithoutState && (
        <div className={styles.globalState}>
          <Text>Appliquer un statut à tous les éléments de la pièce</Text>
          <EdlEtatPills
            label="Statut de tous les éléments"
            value={item?.note}
            onChange={applyNoteToAll}
          />
        </div>
      )}

      {TYPE_EDLS.map((typeEdl) => {
        const childItems = items
          .map((child, index) => ({ child, index }))
          .filter(({ child }) => typeEdl.type === child.type);
        const typeIndexes = childItems.map(({ index }) => index);

        function handleDragEnd({ active, over }: DragEndEvent) {
          if (!over || active.id === over.id) return;
          const from = typeIndexes.indexOf(active.id as number);
          const to = typeIndexes.indexOf(over.id as number);
          if (from === -1 || to === -1) return;
          const next = moveWithinType(items, typeIndexes, from, to);
          setItems(next);
          if (openIndex !== null) {
            setOpenIndex(next.indexOf(items[openIndex]));
          }
        }

        const importableRooms = otherRooms
          .map((room) => ({
            room,
            elements: (room.items ?? []).filter(
              (child) => child.type === typeEdl.type,
            ),
          }))
          .filter(({ elements }) => elements.length > 0);

        return (
          <section key={typeEdl.type} className={styles.section}>
            <div className={styles.sectionHeader}>
              <Title
                variant="deca"
                weight="bold"
                as="h3"
                className={styles.sectionTitle}
              >
                {typeEdl.nom}
              </Title>
              <Text variant="deci" as="span">
                {childItems.length} élément{childItems.length > 1 ? "s" : ""}
              </Text>
              {!isTemplate && importableRooms.length > 0 && (
                <DropdownMenu
                  align="end"
                  trigger={
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={IconFileImport}
                      className={styles.sectionAction}
                    >
                      Importer depuis une autre pièce
                    </Button>
                  }
                >
                  {importableRooms.map(({ room, elements }) => (
                    <DropdownMenu.Item
                      key={room.id}
                      label={`${room.nom} · ${elements.length} élément${
                        elements.length > 1 ? "s" : ""
                      }`}
                      onClick={() => {
                        setItems([
                          ...items.filter(
                            (child) => child.type !== typeEdl.type,
                          ),
                          ...elements.map((element) =>
                            cloneElement(fromApi(element)),
                          ),
                        ]);
                        setOpenIndex(null);
                      }}
                    />
                  ))}
                </DropdownMenu>
              )}
            </div>

            <FieldArray
              name={`${inputNamePrefix}items`}
              render={() => (
                <div className={styles.sectionContent}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={typeIndexes}
                      strategy={verticalListSortingStrategy}
                    >
                      {childItems.map(({ child, index }) => (
                        <SortableElement key={index} id={index}>
                          {(handle) => (
                            <EdlItemNomEtatInputs
                              isChild
                              item={child}
                              inputNamePrefix={`${inputNamePrefix}items[${index}].`}
                              onDelete={() => removeElement(index)}
                              onCopy={() =>
                                appendElement(
                                  cloneElement(child, `${child.nom} - copie`),
                                )
                              }
                              isTemplate={isTemplate}
                              dragHandle={
                                !isTemplate && childItems.length > 1 && handle
                              }
                              open={openIndex === index}
                              onToggle={() =>
                                setOpenIndex(openIndex === index ? null : index)
                              }
                            />
                          )}
                        </SortableElement>
                      ))}
                    </SortableContext>
                  </DndContext>
                  <EdlAddButton
                    onClick={() =>
                      appendElement(
                        isTemplate
                          ? {
                              ...EDL_ITEM_TEMPLATE_CHILD_VALUE,
                              type: typeEdl.type,
                            }
                          : {
                              ...EDL_ITEM_CHILD_VALUE,
                              note: item?.note?.toString(),
                              type: typeEdl.type,
                            },
                      )
                    }
                  >
                    Ajouter un élément
                  </EdlAddButton>
                </div>
              )}
            />
          </section>
        );
      })}

      {!isTemplate && (
        <div className={styles.extras}>
          <Title
            variant="deca"
            weight="bold"
            as="h3"
            className={styles.sectionTitle}
          >
            Photos d'ensemble de la pièce
          </Title>
          <EdlPhotoField
            path={`${inputNamePrefix}photos`}
            text="Ajouter des photos de la pièce"
          />
          <Text variant="deci" color="muted">
            La photo la plus à gauche sert de couverture. Glissez-déposez pour
            réorganiser. <br />
            Astuce : prenez les photos depuis l'appareil photo du téléphone,
            puis importez-les toutes d'un coup.
          </Text>
          <TextArea
            type="text"
            name={`${inputNamePrefix}commentaire`}
            placeholder="Commentaires"
            marginForm=""
          />
        </div>
      )}
    </div>
  );
}
